import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Plus, Minus, RefreshCw, ShieldCheck, Trash2, Mail } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../../lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const fmt = (n, d = 2) => Number.isFinite(+n) ? (+n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d }) : "—";

export default function CustomersManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjOpen, setAdjOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ ngn_delta: "", cad_delta: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/customers");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openAdjust = (u) => {
    setTarget(u);
    setForm({ ngn_delta: "", cad_delta: "", notes: "" });
    setAdjOpen(true);
  };

  const saveAdjust = async () => {
    if (!target) return;
    const ngn = parseFloat(form.ngn_delta);
    const cad = parseFloat(form.cad_delta);
    const body = { notes: form.notes || null };
    if (Number.isFinite(ngn) && ngn !== 0) body.wallet_ngn_delta = ngn;
    if (Number.isFinite(cad) && cad !== 0) body.wallet_cad_delta = cad;
    if (!("wallet_ngn_delta" in body) && !("wallet_cad_delta" in body)) {
      toast.error("Enter a non-zero NGN or CAD amount");
      return;
    }
    setBusy(true);
    try {
      await adminApi.put(`/admin/customers/${target.id}/wallet`, body);
      toast.success("Wallet updated");
      setAdjOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const filtered = !search.trim() ? items : items.filter((u) => {
    const s = search.toLowerCase();
    return [u.email, u.first_name, u.last_name, u.phone].filter(Boolean).some((v) => v.toLowerCase().includes(s));
  });

  return (
    <div data-testid="customers-manager-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-display text-lg font-semibold text-[#0B192C]">Customers ({items.length})</h3>
        <div className="flex gap-2 items-center">
          <Input placeholder="Search name/email/phone…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="customers-search" className="h-9 rounded-full w-64" />
          <Button variant="outline" onClick={load} className="rounded-full h-9 px-4 text-sm" data-testid="customers-refresh">
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-slate-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-slate-500 text-sm" data-testid="customers-empty">No customers yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">CAD wallet</th>
                  <th className="px-4 py-3">NGN wallet</th>
                  <th className="px-4 py-3">KYC</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((u) => (
                  <tr key={u.id} data-testid={`customer-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-display font-semibold text-[#0B192C]">{u.first_name} {u.last_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{u.id.slice(0, 8)}…</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700">{u.email}</div>
                      <div className="text-xs text-slate-500">{u.phone}</div>
                    </td>
                    <td className="px-4 py-3 font-display font-semibold text-[#0B192C]" data-testid={`customer-cad-${u.id}`}>${fmt(u.wallet_cad)}</td>
                    <td className="px-4 py-3 font-display font-semibold text-[#0B192C]" data-testid={`customer-ngn-${u.id}`}>₦{fmt(u.wallet_ngn)}</td>
                    <td className="px-4 py-3">
                      <Select value={u.kyc_status} onValueChange={async (v) => {
                        try { await adminApi.put(`/admin/customers/${u.id}/kyc`, { kyc_status: v }); toast.success(`KYC → ${v}`); load(); }
                        catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
                      }}>
                        <SelectTrigger className="h-9 w-36 rounded-lg" data-testid={`customer-kyc-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unverified">Unverified</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="verified">Verified</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openAdjust(u)} data-testid={`customer-adjust-${u.id}`} className="rounded-full h-8 text-xs">
                          <Wallet size={12} className="mr-1" /> Adjust
                        </Button>
                        <Button size="sm" variant="outline" onClick={async () => {
                          try { const { data } = await adminApi.post(`/admin/customers/${u.id}/send-welcome`); toast[data?.sent ? "success" : "warning"](data?.sent ? "Welcome email sent" : "Email service not ready (check RESEND_API_KEY)."); }
                          catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
                        }} data-testid={`customer-welcome-${u.id}`} className="rounded-full h-8 text-xs">
                          <Mail size={12} className="mr-1" /> Welcome
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="p-2 text-slate-400 hover:text-red-600" data-testid={`customer-delete-${u.id}`} aria-label="Delete">
                              <Trash2 size={14} />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this customer?</AlertDialogTitle>
                              <AlertDialogDescription>
                                {u.first_name} {u.last_name} ({u.email}) — their account and all transactions will be permanently removed. Cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={async () => {
                                try { await adminApi.delete(`/admin/customers/${u.id}`); toast.success("Deleted"); load(); }
                                catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
                              }} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent data-testid="wallet-adjust-dialog">
          <DialogHeader>
            <DialogTitle>Adjust wallet — {target?.first_name} {target?.last_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Current: <span className="font-semibold text-[#0B192C]">${fmt(target?.wallet_cad)}</span> · <span className="font-semibold text-[#0B192C]">₦{fmt(target?.wallet_ngn)}</span></p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>CAD change (+/−)</Label>
                <Input type="number" step="0.01" value={form.cad_delta} onChange={(e) => setForm({ ...form, cad_delta: e.target.value })} placeholder="e.g. 100 or -50" data-testid="adjust-cad-input" className="mt-1.5 h-10 rounded-lg" />
              </div>
              <div>
                <Label>NGN change (+/−)</Label>
                <Input type="number" step="0.01" value={form.ngn_delta} onChange={(e) => setForm({ ...form, ngn_delta: e.target.value })} placeholder="e.g. 50000 or -10000" data-testid="adjust-ngn-input" className="mt-1.5 h-10 rounded-lg" />
              </div>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Cash deposit received" className="mt-1.5 h-10 rounded-lg" />
            </div>
            <p className="text-xs text-slate-500">Positive numbers credit the wallet (<Plus size={10} className="inline" />); negative numbers debit (<Minus size={10} className="inline" />). Balances cannot go below zero.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={saveAdjust} disabled={busy} data-testid="adjust-save-button" className="rounded-full bg-[#FF6B00] hover:bg-[#E65C00] font-semibold">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
